import type { ReactNode } from "react";
import type { JSX } from "react";

import type { INavItemBadgeType } from "@similarweb/ui-components";
import { SolutionItem } from "@similarweb/ui-components";

import type { IAvailableSolutions, ISubscriptionSolution, IUserData } from "app/user-data/solutionBaseInterfaces";
import { getReturnUrl, getSwitchAccountUrl } from "app/user-data/Solutions";
import { hasAccessToPackage } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import { SolutionItemDropdown } from "app/components/sidebar3/primary-side-bar/secondary-actions/Solutions/SolutionItemDropdown";
import {
    ApiIcon,
    AppIntelligenceIcon,
    MarketplaceIntelligenceIcon,
    SalesIntelligenceIcon,
    StockIntelligenceIcon,
    WebIntelligenceIcon,
} from "app/components/sidebar3/primary-side-bar/secondary-actions/Solutions/SolutionsIcons";
import { isAppAnalysisPage } from "app/components/sidebar3/primary-side-bar/utils";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FORCE_PRO_PARAM } from "single-spa/react-app/router/NavigationUtils";
import "../../../../React/SolutionSwitcherMenuComponent/SolutionSwitcherMenu.scss";
import { allTrackers } from "app/services/track/track";

import { SolutionsText, SolutionGroupWrapper, Wrapper } from "./SolutionsSwitcherMenuComponent.styles";

const SOLUTION_TRACKING_EVENT_NAMES: Partial<Record<string, string>> = {
    "sidebar.solutions.DRI": "Settings_my_solutions/Digital Research Intelligence",
    "sidebar.solutions.app": "Settings_my_solutions/App Intelligence",
    "sidebar.solutions.shopper": "Settings_my_solutions/Shopper Intelligence",
    "sidebar.solutions.SI": "Settings_my_solutions/Sales Intelligence",
    "sidebar.solutions.stock": "Settings_my_solutions/Stock Intelligence",
    "sidebar.solutions.API": "Settings_my_solutions/API Solution",
};

const trackSolutionClick = (trackingKey?: string) => {
    const eventName = trackingKey && SOLUTION_TRACKING_EVENT_NAMES[trackingKey];
    if (eventName) {
        allTrackers.dispatchTrackEvent("Button", "click", eventName);
    }
};

enum ESolutionsType {
    MySolutions = "mySolutions",
    DiscoverSolutions = "discoverSolutions",
}

export interface IAvailableSolutionsNewSidebar extends IAvailableSolutions {
    icon: ReactNode;
    isSelected?: boolean;
    isHidden?: boolean;
    badgeType?: INavItemBadgeType;
}

const SOLUTIONS_GROUP_DEFAULT_MARGIN_BOTTOM = 12;

const getUrlOrHandler = (sol: IAvailableSolutions): string | (() => void) => {
    const switchUrl = getSwitchAccountUrl(sol);
    return switchUrl ?? getReturnUrl(sol);
};

interface ISolutionsSwitcherMenuComponent {
    subscriptionSolution: ISubscriptionSolution[];
}

export const SolutionsSwitcherMenuComponent: React.FC<ISolutionsSwitcherMenuComponent> = ({
    subscriptionSolution,
}): JSX.Element => {
    const linkTarget = "_self";

    const getUsersInThisSolution = (solutionName): IUserData[] => {
        const result = [];
        subscriptionSolution?.forEach((item) => {
            if (item.Solutions.includes(solutionName)) {
                result.push({ userId: item.UserId, name: item.AccountName, solutionName: solutionName });
            }
        });
        return result;
    };

    const availableSolutions: IAvailableSolutionsNewSidebar[] = [
        {
            solutionTitle: "settings.solution_switcher.web.title",
            solutionText: "settings.solution_switcher.web.text",
            linkTarget,
            url: `${swSettings.swsites.pro}?${FORCE_PRO_PARAM}=true`,
            itemKey: "web-solution",
            icon: <WebIntelligenceIcon />,
            isSelected: !isAppAnalysisPage() && !proNavigator.isSalesIntelligence(),
            isInMySolutions: swSettings.user.hasMR || swSettings.user.hasDM,
            HookState: "WebMarketAnalysisOverviewHomepage",
            trackingKey: "sidebar.solutions.DRI",
            usersInMySolution: getUsersInThisSolution("web-solution"),
        },
        {
            solutionTitle: "settings.solution_switcher.app.title",
            solutionText: "settings.solution_switcher.app.text",
            linkTarget,
            url: swSettings.swsites.apps,
            itemKey: "app-solution",
            icon: <AppIntelligenceIcon />,
            isInMySolutions: getUsersInThisSolution("app-solution").length > 0,
            trackingKey: "sidebar.solutions.app",
            usersInMySolution: getUsersInThisSolution("app-solution"),
        },
        {
            solutionTitle: "settings.solution_switcher.shopper.title",
            solutionText: "settings.solution_switcher.shopper.text",
            linkTarget,
            url: swSettings.swsites.shopper,
            itemKey: "shopper-solution",
            icon: <MarketplaceIntelligenceIcon />,
            isInMySolutions: swSettings.user.applications.includes("Datafactory"),
            trackingKey: "sidebar.solutions.shopper",
            usersInMySolution: getUsersInThisSolution("shopper-solution"),
        },
        {
            solutionTitle: "settings.solution_switcher.sales.title",
            solutionText: "settings.solution_switcher.sales.text",
            linkTarget,
            url: ({ isInMySolutions }) =>
                getUsersInThisSolution("sales-solution").length > 0 || isInMySolutions
                    ? "#/sales"
                    : `${swSettings.swsites.light}/corp/sales/`,
            itemKey: "sales-solution",
            isSelected: !isAppAnalysisPage() && proNavigator.isSalesIntelligence(),
            icon: <SalesIntelligenceIcon />,
            isInMySolutions: hasAccessToPackage("salesIntelligence"),
            trackingKey: "sidebar.solutions.SI",
            usersInMySolution: getUsersInThisSolution("sales-solution"),
        },
        {
            solutionTitle: "settings.solution_switcher.stock.title",
            solutionText: "settings.solution_switcher.stock.text",
            linkTarget,
            url: swSettings.swsites.investors,
            itemKey: "stock-solution",
            icon: <StockIntelligenceIcon />,
            isInMySolutions: swSettings.user.applications.includes("Investors"),
            trackingKey: "sidebar.solutions.stock",
            usersInMySolution: getUsersInThisSolution("stock-solution"),
        },
        {
            solutionText: "",
            solutionTitle: "settings.solution_switcher.data_exporter.title",
            linkTarget,
            url: swSettings.swsites.datahub,
            itemKey: "api-solution",
            icon: <ApiIcon />,
            isInMySolutions: true,
            trackingKey: "sidebar.solutions.API",
            badgeType: "new",
            usersInMySolution: getUsersInThisSolution("api-solution"),
        },
    ];

    const getSolComponents = (solutions: IAvailableSolutionsNewSidebar[], solutionsType: ESolutionsType) => {
        return solutions.map((sol: IAvailableSolutionsNewSidebar) => {
            const urlOrHandler = getUrlOrHandler(sol);
            const { solutionTitle, solutionText, trackingKey, usersInMySolution } = sol;
            if (usersInMySolution.length > 1) {
                return (
                    <SolutionItemDropdown
                        sol={sol}
                        key={solutionsType + solutionText}
                        title={translate(solutionTitle)}
                        text={translate(solutionText)}
                        onClick={() => {
                            trackSolutionClick(trackingKey);
                        }}
                        urlOrHandler={urlOrHandler}
                    ></SolutionItemDropdown>
                );
            }
            return (
                <SolutionItem
                    {...sol}
                    key={solutionsType + solutionText}
                    title={translate(solutionTitle)}
                    text={translate(solutionText)}
                    urlOrHandler={urlOrHandler}
                    onClick={() => {
                        trackSolutionClick(trackingKey);
                    }}
                ></SolutionItem>
            );
        });
    };

    const mySolutions: IAvailableSolutionsNewSidebar[] = [];
    const discoverSolutions: IAvailableSolutionsNewSidebar[] = [];

    availableSolutions.forEach((sol) => {
        if (sol.isHidden) {
            return;
        }

        if (sol.usersInMySolution.length > 0 || sol.isInMySolutions) {
            mySolutions.push(sol);
        } else {
            discoverSolutions.push(sol);
        }
    });

    const mySolComponents = getSolComponents(mySolutions, ESolutionsType.MySolutions);
    const discoverSolutionsComponents = getSolComponents(discoverSolutions, ESolutionsType.DiscoverSolutions);

    const getDropdownContent = (): JSX.Element[] => {
        const solutions = [];
        const discoverSolutionsCount = discoverSolutionsComponents.length;
        const displayDiscoverGroup = discoverSolutionsCount > 0;

        if (mySolComponents.length > 0) {
            solutions.push([
                <SolutionsText key="my-solutions-title">{translate("solution_switcher.my_solutions")}</SolutionsText>,
                <SolutionGroupWrapper
                    key="new-solutions-group-wrap"
                    marginBottom={displayDiscoverGroup ? SOLUTIONS_GROUP_DEFAULT_MARGIN_BOTTOM : 0}
                >
                    {mySolComponents}
                </SolutionGroupWrapper>,
            ]);
        }

        if (displayDiscoverGroup) {
            solutions.push([
                <SolutionsText key="new-solutions-title">{translate("solution_switcher.discover")}</SolutionsText>,
                <SolutionGroupWrapper key="new-solutions-group-wrap">
                    {discoverSolutionsComponents}
                </SolutionGroupWrapper>,
            ]);
        }

        return solutions;
    };

    return <Wrapper>{...getDropdownContent()}</Wrapper>;
};
